// @flow

import { Component } from 'react';
import classNames from 'classnames';
import root from './helpers/windowCheck';

// Utils
import {
    getElementHeight,
    getElementPosition,
    didSettingsChange,
    getInitialSettings,
    getWindowWidth,
    getWindowScrollPosition,
} from './helpers/utils';

// Types
import type {
    ProfileCoverProps,
    ProfileCoverState,
    ProfileCoverSettings,
    ProfileCoverLayoutProps,
} from './types';

// Component
import styles from './styles.scss';
import ProfileCoverLayout from './ProfileCoverLayout';

class ProfileCover extends Component<ProfileCoverProps, ProfileCoverState> {
    static defaultProps: {
        fullStateSize: number,
        stickyStateSize: number,
        stickyWidthThreshold: number,
    };

    constructor(props: ProfileCoverProps) {
        super(props);

        this.settings = getInitialSettings(props);
        this.state = {
            isSticky: false,
            size: this.settings.fullStateSize,
        };

        this.wrapperElement = null;

        // bind methods to instance
        this._updateSettingsBound = this._updateSettings.bind(this);
        this._updateProfileStateBound = this._updateProfileState.bind(this);
    }
    state: ProfileCoverState;

    componentDidMount() {
        this._updateSettings();

        // Add event listeners to 'scroll' and 'resize' events
        if (!this.settings.disableSticky) {
            root.addEventListener('scroll', this._updateProfileStateBound);
            root.addEventListener('resize', this._updateProfileStateBound);
        }
    }

    UNSAFE_componentWillReceiveProps(props: ProfileCoverProps) {
        if (didSettingsChange(this.props, props)) {
            root.removeEventListener('scroll', this._updateProfileStateBound);
            root.removeEventListener('resize', this._updateProfileStateBound);

            this.settings = getInitialSettings(props);
            if (!this.settings.disableSticky) {
                root.addEventListener('scroll', this._updateProfileStateBound);
                root.addEventListener('resize', this._updateProfileStateBound);
            }

            this.setState(
                {
                    isSticky: false,
                    size: this.settings.fullStateSize,
                },
                this._updateSettingsBound
            );
        }
    }

    componentWillUnmount() {
        // Remove event listeners (cleanup)
        root.removeEventListener('scroll', this._updateProfileStateBound);
        root.removeEventListener('resize', this._updateProfileStateBound);
    }

    props: ProfileCoverProps;
    _updateSettingsBound: () => void;
    _updateProfileStateBound: () => void;
    settings: ProfileCoverSettings;
    wrapperElement: ?HTMLElement;

    _updateSettings(): void {
        const wrapperElement = this.wrapperElement;

        if (wrapperElement) {
            const fullElHeight: number = getElementHeight(
                wrapperElement.getElementsByClassName(styles.cover)[0]
            );
            wrapperElement.style.height = `${fullElHeight}px`;
            this.settings.stickyHeightThreshold = getElementPosition(
                wrapperElement.getElementsByClassName(styles.bottom)[0]
            );

            // update the profile state
            this._updateProfileState();
        }
    }

    _updateProfileState(): void {
        if (this.settings.disableSticky) {
            return;
        }

        const winWidth: number = getWindowWidth();
        const winScrollY: number = getWindowScrollPosition();
        const {
            fullStateSize,
            stickyStateSize,
            stickyWidthThreshold,
            stickyHeightThreshold,
        }: ProfileCoverSettings = this.settings;

        const wasSticky: boolean = this.state.isSticky;
        const isSticky: boolean =
            winWidth > stickyWidthThreshold && winScrollY > stickyHeightThreshold;

        if (!isSticky) {
            // Sticky => Full
            if (wasSticky) {
                const callback = this.props.onUnStick ? this.props.onUnStick : void 0;
                this.setState({ isSticky: false, size: fullStateSize }, callback);
            }
        } else {
            let size: number = fullStateSize + stickyHeightThreshold - winScrollY;
            if (size < stickyStateSize) {
                size = stickyStateSize;
            } else if (size > fullStateSize) {
                size = fullStateSize;
            }

            // Sticky / Full => Sticky
            if (!wasSticky || size !== this.state.size) {
                const callback = !wasSticky && this.props.onStick ? this.props.onStick : void 0;

                this.setState({ isSticky: true, size }, callback);
            }
        }
    }

    _getLayoutProps(): ProfileCoverLayoutProps {
        const classes: Object = this.props.additionalClasses || {};
        const isSticky: boolean = !this.settings.disableSticky && this.state.isSticky;

        return {
            width: this.state.size,
            coverPhoto: this.props.coverPhoto,
            className: classNames(styles.cover, classes.cover, {
                [classNames(styles.sticky, classes.sticky)]: isSticky,
                [classNames(styles.noPicture, classes.noPicture)]: !this.props.PictureRenderer,
            }),
            components: {
                TopRenderer: this.props.TopRenderer,
                TabsRenderer: this.props.TabsRenderer,
                SocialRenderer: this.props.SocialRenderer,
                PictureRenderer: this.props.PictureRenderer,
                DetailsRenderer: this.props.DetailsRenderer,
            },
        };
    }

    render() {
        return (
            <div
                className={styles.wrapper}
                ref={el => {
                    this.wrapperElement = el;
                }}
            >
                <ProfileCoverLayout {...this._getLayoutProps()} />
            </div>
        );
    }
}

ProfileCover.defaultProps = {
    fullStateSize: 160,
    stickyStateSize: 60,
    stickyWidthThreshold: 1024,
};

export default ProfileCover;
